import { useState, type ReactNode } from "react"
import { Box, HStack, Icon, Text } from "@chakra-ui/react"
import { FaCheck } from "react-icons/fa6"
import type { AlertRuleState } from "@hydrologiq/shared/components/alerts"
import { AlertSettingsCard, EDITING_DIVIDER } from "@hydrologiq/shared/components/alerts/AlertSettingsCard"
import {
  MUTED,
  OriginTag,
  SeverityTag,
  USER_DEFINED,
  formatValue,
} from "@hydrologiq/shared/components/alerts/alertSummary"
import { formatChipLabel, getTimeWindowDisplayValue } from "@hydrologiq/shared/components/alerts/alertRuleFormat"
import { customAlertField, operatorWithSymbol } from "@hydrologiq/shared/components/alerts/customAlertFields"
import { useAlertRuleControls, type AlertRuleHandlers } from "@hydrologiq/shared/components/alerts/useAlertRuleControls"
import { ActionButton } from "@hydrologiq/shared/content/components/Button"
import { AlertConditionRows } from "./AlertConditionRows"
import { COMPARISON_WIDTH, FIELD_WIDTH, LockedBox, UnitInput } from "./UnitInput"

const TIME_UNITS = { h: "h", m: "min", s: "s" } as const

export const isCustom = (rule: AlertRuleState) => rule.template.origin?.toUpperCase() === "CUSTOM"

/** Whether Edit has anything to change: a Custom Alert, or a Standard Alert's threshold. */
export const canEditRule = (rule: AlertRuleState) =>
  isCustom(rule) || (rule.template.overridable && rule.effectiveThreshold != null)

export interface AssetAlertCardProps extends AlertRuleHandlers {
  rule: AlertRuleState
  /** e.g. the Module chip on an alert that bubbled up from a Module */
  extraChips?: ReactNode
  /** Names of the Assets and Modules grouped conditions refer to */
  assetNames?: Record<string, string>
  onEdit?: () => void
  onDelete?: () => void
  /** Just out of edit state */
  highlighted?: boolean
}

/** One saved alert on an Asset's Alert Configuration tab. */
export function AssetAlertCard({
  rule,
  extraChips,
  assetNames,
  onEdit,
  onDelete,
  highlighted,
  ...handlers
}: AssetAlertCardProps) {
  const { template, isEnabled } = rule
  const { isToggling, handleToggle } = useAlertRuleControls(rule, handlers)
  const custom = isCustom(rule)

  return (
    <AlertSettingsCard
      label={template.name}
      title={template.name}
      isEnabled={isEnabled}
      chips={<RuleChips rule={rule} extraChips={extraChips} />}
      summary={<AlertConditionRows rule={rule} custom={custom} assetNames={assetNames} />}
      // An event-based alert's description is already its condition box
      description={custom || rule.effectiveThreshold == null ? null : template.description}
      controls={{ onToggle: handleToggle, toggling: isToggling, onEdit, onDelete }}
      highlighted={highlighted}
    />
  )
}

function RuleChips({ rule, extraChips }: { rule: AlertRuleState; extraChips?: ReactNode }) {
  return (
    <>
      <OriginTag custom={isCustom(rule)} />
      <SeverityTag severity={rule.template.severity} />
      {extraChips}
    </>
  )
}

export interface StandardAlertEditorProps extends AlertRuleHandlers {
  rule: AlertRuleState
  extraChips?: ReactNode
  /** After a save */
  onClose: () => void
  onCancel: () => void
}

/**
 * A Standard Alert's card while it's edited: what it watches is fixed, its threshold (and
 * time window, where it has one) can change. Reset puts the defaults back in the draft;
 * Save stores the draft, Cancel discards it.
 */
export function StandardAlertEditor({
  rule,
  extraChips,
  onClose,
  onCancel,
  ...handlers
}: StandardAlertEditorProps) {
  const { template } = rule
  const {
    showAsOverridden,
    displayThreshold,
    displayTimeWindow,
    timeWindowUnit,
    handleThresholdCommit,
    handleTimeWindowCommit,
    handleReset,
  } = useAlertRuleControls(rule, handlers)
  const [threshold, setThreshold] = useState(displayThreshold)
  const [timeWindow, setTimeWindow] = useState(displayTimeWindow ?? "")
  const [saving, setSaving] = useState(false)

  const unit = template.thresholdUnit ?? customAlertField(template.metric)?.unit ?? ""
  const defaultThreshold = template.threshold
  const defaultTimeWindow =
    template.timeWindowSeconds != null ? getTimeWindowDisplayValue(template.timeWindowSeconds).value.toString() : ""
  // What Save would store: the draft's values against the template's
  const atDefaults =
    (threshold === "" || Number(threshold) === defaultThreshold) &&
    (displayTimeWindow == null || timeWindow === defaultTimeWindow)
  const userDefined = !atDefaults
  // What it watches and how it compares are fixed; only the values change
  const field = customAlertField(template.metric)?.label ?? (formatChipLabel(template.metric) || "Threshold")
  const comparison = operatorWithSymbol(template.operator) || template.thresholdDirection?.toLowerCase() || "—"

  const save = async () => {
    setSaving(true)
    let saved: boolean
    if (atDefaults) {
      // Back to the template's values: clear the overrides, if there are any to clear
      saved = !showAsOverridden || !rule.assignment || (await handleReset())
    } else {
      const num = parseFloat(threshold)
      // Same bounds the Systems card applies
      const bounded = isNaN(num) ? threshold : String(Math.min(Math.max(num, 0), unit === "%" ? 100 : Infinity))
      const thresholdSaved = await handleThresholdCommit(bounded)
      saved = thresholdSaved && (displayTimeWindow == null || (await handleTimeWindowCommit(timeWindow)))
    }
    setSaving(false)
    // Stay open on a failure: the toast says what went wrong, the values are still here
    if (saved) onClose()
  }

  // Like any other change, the defaults are only stored on Save
  const reset = () => {
    setThreshold(defaultThreshold?.toString() ?? "")
    setTimeWindow(defaultTimeWindow)
  }

  return (
    <AlertSettingsCard
      label={`Edit ${template.name}`}
      title={template.name}
      editing
      isEnabled={false}
      chips={<RuleChips rule={rule} extraChips={extraChips} />}
      description={template.description}
    >
      <Text fontSize="md" fontWeight="semibold" mb={2}>
        Conditions
      </Text>
      <Box display="flex" flexDirection="column" gap={2}>
        <HStack gap={3} flexWrap="wrap">
          <LockedBox w={FIELD_WIDTH}>{field}</LockedBox>
          <LockedBox w={COMPARISON_WIDTH}>{comparison}</LockedBox>
          <UnitInput
            value={threshold}
            unit={unit}
            ariaLabel={`Threshold for ${template.name}`}
            tone={userDefined ? "userDefined" : "neutral"}
            onChange={setThreshold}
          />
          {userDefined ? (
            <HStack gap={3}>
              <Text fontSize="sm" fontWeight="semibold" color={USER_DEFINED}>
                User-defined
              </Text>
              {defaultThreshold != null && (
                <Box
                  as="button"
                  onClick={reset}
                  title={`Reset to ${formatValue(defaultThreshold, unit)}`}
                  aria-label={`Reset ${template.name} to ${formatValue(defaultThreshold, unit)}`}
                  fontSize="sm"
                  color="#39aed2"
                  cursor="pointer"
                  _hover={{ color: "#0bc5ea" }}
                >
                  Reset
                </Box>
              )}
            </HStack>
          ) : (
            <Text fontSize="sm" color={MUTED}>
              Default
            </Text>
          )}
        </HStack>
        {displayTimeWindow != null && (
          <HStack gap={3}>
            {/* Qualifies the threshold ("5% within 10 min"); it isn't a second condition */}
            <LockedBox w={FIELD_WIDTH}>Within</LockedBox>
            <Box w={COMPARISON_WIDTH} flexShrink={0} />
            <UnitInput
              value={timeWindow}
              unit={TIME_UNITS[timeWindowUnit]}
              ariaLabel={`Time window for ${template.name}`}
              onChange={setTimeWindow}
            />
          </HStack>
        )}
      </Box>
      <HStack justify="space-between" gap={3} mt={4} pt={4} borderTop="1px solid" borderColor={EDITING_DIVIDER}>
        <Text fontSize="sm" color={MUTED}>
          Standard alerts are set by Logiq Systems. You can change threshold values only.
        </Text>
        <HStack gap={3}>
          <ActionButton onClick={onCancel} context={{ label: "Cancel", description: "", variant: "secondary" }} />
          <ActionButton onClick={save} disabled={saving} context={{ label: "Save", description: "", variant: "primary" }}>
            <Icon as={FaCheck} boxSize="12px" />
            Save
          </ActionButton>
        </HStack>
      </HStack>
    </AlertSettingsCard>
  )
}

export default AssetAlertCard
